/**
 * @spec docs/superpowers/specs/2026-10-15-staking-ui-wallet-actions-design.md §7.3
 * @standard docs/standards/staking-ui-wallet-actions.md#errors
 *
 * Every wallet failure leaves sign.ts and send.ts as one error type that knows its phase, wallet
 * kind and required chain. The provider's own error stays as `cause`, so classifySigningError can
 * still read its codes and wording; the message is the provider's, so text shown by code that
 * does not use describeWalletError yet is unchanged.
 */
import { classifySigningError } from '@/lib/signing-errors';
import { WalletActionError, type WalletActionPhase } from './errors';
import type { PreparedWallet } from './prepare';

const NO_MESSAGE = 'Your wallet could not complete the request.';

function providerMessage(error: unknown): string {
  if (error instanceof Error && error.message.length > 0) return error.message;
  if (typeof error === 'object' && error !== null) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return NO_MESSAGE;
}

export function toWalletActionError(
  error: unknown,
  p: PreparedWallet,
  phase: WalletActionPhase
): WalletActionError {
  if (error instanceof WalletActionError) return error;
  const requiredChainId = p.action.chainId;
  return new WalletActionError({
    message: providerMessage(error),
    phase,
    category: classifySigningError(error, { requiredChainId }).category,
    walletKind: p.walletKind,
    requiredChainId,
    cause: error,
  });
}
